import { useState, useEffect, useCallback } from 'react';
import { governance } from '../../services/governanceApi';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700', in_review: 'bg-blue-100 text-blue-700',
  voting: 'bg-purple-100 text-purple-700', approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700', executed: 'bg-gray-100 text-gray-600',
};
const PRIORITY_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700' };

function extractItems(itemsData) {
  // API returns { success, items: { items: [...], total } } or { success, items: [...] }
  if (!itemsData?.items) return [];
  if (Array.isArray(itemsData.items)) return itemsData.items;
  if (Array.isArray(itemsData.items.items)) return itemsData.items.items;
  return [];
}

function CommitteeList({ userEmail, onSelectCommittee }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemCounts, setItemCounts] = useState({}); // { committeeId: { pending, total } }
  const [applyingTo, setApplyingTo] = useState(null);
  const [applicationText, setApplicationText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applyError, setApplyError] = useState('');

  useEffect(() => {
    governance.getCommittees(userEmail).then(d => {
      setData(d);
      // Fetch item counts per committee
      if (d?.committees) {
        for (const c of d.committees) {
          governance.getItems({ committee_id: c.id, user_email: userEmail })
            .then(r => {
              const list = extractItems(r);
              const pending = list.filter(i => ['pending', 'in_review', 'voting'].includes(i.status)).length;
              setItemCounts(prev => ({ ...prev, [c.id]: { pending, total: list.length } }));
            }).catch(() => {});
        }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userEmail]);

  const handleApply = async () => {
    if (applicationText.trim().length < 20) {
      setApplyError('Please write at least a few sentences about why you want to join.');
      return;
    }
    setSubmitting(true);
    setApplyError('');
    try {
      await governance.joinCommittee(applyingTo.id, userEmail, applicationText.trim());
      setApplyingTo(null);
      setApplicationText('');
      governance.getCommittees(userEmail).then(d => setData(d));
    } catch (err) {
      setApplyError(err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading committees...</div>;
  if (!data?.committees?.length) return <div className="p-8 text-center text-gray-400">No committees yet.</div>;

  const myIds = data.userCommittees || [];
  const pendingIds = data.pendingCommittees || [];

  return (
    <>
      <div className="space-y-3">
        {/* My committees */}
        {data.committees.filter(c => myIds.includes(c.id)).length > 0 && (
          <div className="mb-2">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Your Committees</h3>
            {data.committees.filter(c => myIds.includes(c.id)).map(c => (
              <div key={c.id} onClick={() => onSelectCommittee(c.id)}
                className="p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow mb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{c.display_name || c.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{c.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {itemCounts[c.id]?.pending > 0 && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                        {itemCounts[c.id].pending} new
                      </span>
                    )}
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Member</span>
                    <span className="text-xs text-gray-400">{c.member_count || 0} members</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Available committees */}
        {data.committees.filter(c => !myIds.includes(c.id)).length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Available Committees</h3>
            {data.committees.filter(c => !myIds.includes(c.id)).map(c => {
              const isPending = pendingIds.includes(c.id);
              return (
                <div key={c.id}
                  className="p-4 bg-white rounded-xl border border-gray-200 hover:shadow-sm transition-shadow mb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{c.display_name || c.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{c.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      {c.name === 'founders' ? (
                        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-500">Invitation Only</span>
                      ) : isPending ? (
                        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">Application Pending</span>
                      ) : (
                        <button onClick={() => { setApplyingTo(c); setApplicationText(''); setApplyError(''); }}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                          Apply to Join
                        </button>
                      )}
                      <span className="text-xs text-gray-400">{c.member_count || 0} members</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Application modal */}
      {applyingTo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Apply to join {applyingTo.display_name || applyingTo.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{applyingTo.description}</p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Why do you want to contribute to this committee? What background do you have to add value?
              </label>
              <textarea
                value={applicationText}
                onChange={(e) => setApplicationText(e.target.value)}
                placeholder="Explain your interest in this committee and what experience, skills, or perspective you would bring..."
                rows={5}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 resize-none"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-400">{applicationText.length} characters</p>
              {applyError && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{applyError}</div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3">
              <button onClick={() => setApplyingTo(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleApply} disabled={submitting || applicationText.trim().length < 20}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors">
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ItemDetail({ itemId, userEmail, onBack, committeeName }) {
  const [item, setItem] = useState(null);
  const [quorum, setQuorum] = useState(null);
  const [comment, setComment] = useState('');
  const [commentLinks, setCommentLinks] = useState([]);
  const [linkInput, setLinkInput] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [voting, setVoting] = useState(false);

  const loadItem = useCallback(async () => {
    const d = await governance.getItem(itemId);
    if (d.success) { setItem(d.item); setQuorum(d.quorum); }
  }, [itemId]);

  useEffect(() => { loadItem(); }, [loadItem]);

  if (!item) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  const comments = item.comments || [];
  const votes = item.votes || [];
  const isVoting = item.status === 'voting';
  const isApproved = item.status === 'approved';
  const cmName = (item.committee_name || committeeName || '').toLowerCase();
  const isCodeRelated = ['product', 'engineering', 'technical'].some(k => cmName.includes(k));

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm mb-4">
        <button onClick={onBack} className="text-blue-600 hover:underline">
          {committeeName || 'Committee'}
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-600 truncate max-w-[300px]">{item.title}</span>
      </nav>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{item.title}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>{item.status}</span>
              {item.priority && <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${PRIORITY_COLORS[item.priority] || ''}`}>{item.priority}</span>}
              {item.type && <span className="text-xs text-gray-400">{item.type}</span>}
            </div>
          </div>
        </div>
        {item.description && <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">{item.description}</p>}
      </div>

      {/* Comments */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Discussion ({comments.length})</h3>
        <div className="space-y-3 mb-4">
          {comments.map(c => (
            <div key={c.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xs font-bold flex-shrink-0">
                {(c.user_email || c.email || '?')[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{c.user_email || c.email || 'Member'}</span>
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{c.content}</p>
                {/* Links */}
                {(c.links || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {c.links.map((link, li) => (
                      <a key={li} href={link.url || link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs hover:underline">
                        🔗 {link.label || link.url || link}
                      </a>
                    ))}
                  </div>
                )}
                {/* Attachments */}
                {(c.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {c.attachments.map((att, ai) => (
                      <span key={ai} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        📎 {att.name || att}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={async () => { await governance.upvoteComment(itemId, c.id, userEmail); loadItem(); }}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary-600 flex-shrink-0 min-h-[44px] min-w-[44px] justify-center">
                ▲ {c.upvotes || 0}
              </button>
            </div>
          ))}
          {comments.length === 0 && <p className="text-sm text-gray-400">No comments yet. Start the discussion.</p>}
        </div>

        {/* Attached links preview */}
        {commentLinks.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {commentLinks.map((link, li) => (
              <span key={li} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                🔗 {link.label || link.url}
                <button onClick={() => setCommentLinks(prev => prev.filter((_, i) => i !== li))} className="text-red-400 hover:text-red-600 ml-1">×</button>
              </span>
            ))}
          </div>
        )}

        {/* Link input */}
        {showLinkInput && (
          <div className="flex gap-2 mb-2">
            <input type="url" value={linkInput} onChange={e => setLinkInput(e.target.value)}
              placeholder="Paste URL..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }} />
            <button onClick={() => {
              if (linkInput.trim()) { setCommentLinks(prev => [...prev, { url: linkInput.trim(), label: linkInput.trim().substring(0, 50) }]); setLinkInput(''); setShowLinkInput(false); }
            }} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Add</button>
            <button onClick={() => { setShowLinkInput(false); setLinkInput(''); }} className="px-3 py-2 text-gray-500 text-sm">Cancel</button>
          </div>
        )}

        {/* Add comment */}
        <div className="flex gap-2">
          <button onClick={() => setShowLinkInput(!showLinkInput)} className="px-2 py-2 border border-gray-300 rounded-lg text-gray-500 hover:text-blue-600 text-sm" title="Add link">🔗</button>
          <input type="text" value={comment} onChange={e => setComment(e.target.value)}
            onKeyDown={async e => {
              if (e.key !== 'Enter' || !comment.trim()) return;
              setSubmitting(true);
              await governance.addComment(itemId, userEmail, comment.trim(), [], commentLinks);
              setComment(''); setCommentLinks([]); loadItem(); setSubmitting(false);
            }}
            placeholder="Add a comment..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }} />
          <button onClick={async () => {
            if (!comment.trim()) return;
            setSubmitting(true);
            await governance.addComment(itemId, userEmail, comment.trim(), [], commentLinks);
            setComment(''); setCommentLinks([]); loadItem(); setSubmitting(false);
          }} disabled={!comment.trim() || submitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            Post
          </button>
        </div>
      </div>

      {/* Admin actions + Move to Vote */}
      {!isVoting && !isApproved && item.status !== 'rejected' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex gap-3 flex-wrap justify-center">
            {isCodeRelated && (
              <button onClick={async () => {
                try {
                  await governance.adminApprove(itemId, userEmail);
                  await governance.triggerAiBuild(item);
                  await governance.setActionStatus(itemId, 'agent_executing');
                } catch {}
                loadItem();
              }} className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Approve & Assign to Agent
              </button>
            )}
            <button onClick={async () => {
              try { await governance.adminApprove(itemId, userEmail); } catch {}
              loadItem();
            }} className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
              Approve
            </button>
            <button onClick={async () => { await governance.moveToVote(itemId); loadItem(); }}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200">
              Move to Vote
            </button>
          </div>
        </div>
      )}

      {isVoting && (
        <div className="bg-purple-50 rounded-xl border border-purple-200 p-4 mb-4">
          <h3 className="font-semibold text-purple-900 mb-3">Cast Your Vote</h3>
          <div className="flex gap-3 flex-wrap">
            {[{ key: 'yes', label: 'Yes', color: 'bg-green-600 hover:bg-green-700' },
              { key: 'no', label: 'No', color: 'bg-red-600 hover:bg-red-700' },
              { key: 'needs_discussion', label: 'Needs Discussion', color: 'bg-amber-500 hover:bg-amber-600' },
            ].map(v => (
              <button key={v.key} disabled={voting} onClick={async () => {
                setVoting(true);
                await governance.castVote(itemId, userEmail, v.key);
                loadItem(); setVoting(false);
              }} className={`flex-1 min-w-[100px] px-4 py-3 text-white rounded-lg font-medium ${v.color} disabled:opacity-50`}>
                {v.label}
              </button>
            ))}
          </div>

          {/* Voting Stats */}
          {quorum && (
            <div className="mt-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="flex-1">
                  <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
                    {quorum.total > 0 && (
                      <>
                        <div className="bg-green-500 transition-all" style={{ width: `${(quorum.approves / quorum.quorumSize) * 100}%` }} />
                        <div className="bg-red-500 transition-all" style={{ width: `${(quorum.rejects / quorum.quorumSize) * 100}%` }} />
                        <div className="bg-amber-400 transition-all" style={{ width: `${(quorum.needsInfo / quorum.quorumSize) * 100}%` }} />
                      </>
                    )}
                  </div>
                </div>
                <span className="text-sm font-semibold text-purple-700">{quorum.total}/{quorum.quorumSize} voted</span>
              </div>
              <div className="flex gap-4 text-xs">
                <span className="text-green-700 font-medium">{quorum.approves} approve</span>
                <span className="text-red-700 font-medium">{quorum.rejects} reject</span>
                <span className="text-amber-700 font-medium">{quorum.needsInfo} discuss</span>
              </div>
              {quorum.reached && <div className="mt-2 text-sm font-semibold text-green-700 bg-green-100 rounded-lg px-3 py-1.5 inline-block">Quorum reached — ready for decision</div>}
              {quorum.total >= quorum.quorumSize && !quorum.reached && (
                <div className="mt-2 text-sm font-semibold text-amber-700 bg-amber-100 rounded-lg px-3 py-1.5 inline-block">All members voted — needs review</div>
              )}
            </div>
          )}

          {/* Individual votes */}
          {votes.length > 0 && (
            <div className="mt-3 space-y-1 border-t border-purple-200 pt-3">
              {votes.map(v => (
                <div key={v.id} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full ${v.vote === 'approve' || v.vote === 'yes' ? 'bg-green-500' : v.vote === 'reject' || v.vote === 'no' ? 'bg-red-500' : 'bg-amber-400'}`} />
                  <span className="text-gray-600 font-medium">{v.user_email || 'Member'}</span>
                  <span className="text-gray-400">voted</span>
                  <span className="font-semibold text-gray-700">{v.vote}</span>
                  {v.reason && <span className="text-gray-400">— {v.reason}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Admin direct approve + Take Action */}
          {/* Admin actions */}
          <div className="mt-4 flex gap-2 flex-wrap items-center border-t border-purple-200 pt-3">
            <button disabled={voting} onClick={async () => {
              setVoting(true);
              try { await governance.adminApprove(itemId, userEmail); } catch {}
              loadItem(); setVoting(false);
            }} className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              Admin Approve
            </button>
            {quorum?.reached && quorum.approves > quorum.rejects && (
              <button disabled={voting} onClick={async () => {
                setVoting(true);
                try { await governance.adminApprove(itemId, userEmail); } catch {}
                loadItem(); setVoting(false);
              }} className="px-4 py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Take Action — Approve
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action Tracking */}
      {isApproved && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-green-900">Approved — Action Required</h3>
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
              item.action_status === 'completed' ? 'bg-green-200 text-green-800' :
              item.action_status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
              item.action_status === 'agent_executing' ? 'bg-purple-200 text-purple-800 animate-pulse' :
              'bg-yellow-200 text-yellow-800'
            }`}>{item.action_status === 'agent_executing' ? 'AI Implementing...' : item.action_status || 'pending'}</span>
          </div>

          {/* AI Agent Status */}
          {item.action_status === 'agent_executing' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-purple-800">AI Agent is implementing this change</span>
              </div>
              <div className="text-xs text-purple-600 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-4 text-center">1.</span> Analyzing requirements...
                  <span className="text-green-600 font-medium">done</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 text-center">2.</span> Making code changes...
                  <span className="text-purple-600 font-medium animate-pulse">in progress</span>
                </div>
                <div className="flex items-center gap-2 text-purple-300">
                  <span className="w-4 text-center">3.</span> Running tests...
                </div>
                <div className="flex items-center gap-2 text-purple-300">
                  <span className="w-4 text-center">4.</span> Deploying...
                </div>
              </div>
            </div>
          )}

          {item.action_status === 'completed' && (
            <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <span className="text-sm font-medium text-green-800">Action completed</span>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {item.action_status !== 'completed' && item.action_status !== 'agent_executing' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-600">Choose how to implement this approved item:</p>
              <div className="flex gap-2 flex-wrap">
                {isCodeRelated && (
                  <button onClick={async () => {
                    try {
                      // 1. Trigger AI agent build via Imagia partner API
                      const buildResult = await governance.triggerAiBuild(item);
                      console.log('[Governance] AI build triggered:', buildResult);

                      // 2. Mark item as agent_executing
                      await governance.setActionStatus(item.id, 'agent_executing');
                      await governance.assignAction(item.id, userEmail);

                      // 3. Add comment noting the AI assignment
                      await governance.addComment(item.id, userEmail,
                        `Assigned to AI Agent for implementation. Build job queued.${buildResult?.job?.id ? ` Job: ${buildResult.job.id}` : ''}`,
                        [], []);
                    } catch (err) {
                      console.error('[Governance] AI build failed:', err);
                    }
                    loadItem();
                  }} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    Assign to AI Agent
                    <span className="text-purple-300 text-[10px] font-normal">(recommended)</span>
                  </button>
                )}
                <button onClick={async () => { await governance.assignAction(item.id, userEmail); loadItem(); }}
                  className={`${isCodeRelated ? '' : 'flex-1'} px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2`}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  Assign to Human
                </button>
              </div>
              {item.assigned_to && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Assigned to: <span className="font-medium">{item.assigned_to_email || 'member'}</span></span>
                  <button onClick={async () => { await governance.completeAction(item.id, 'Completed'); loadItem(); }}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
                    Mark Complete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CommitteeDetail({ committeeId, userEmail, onBack, selectedItemId, onSelectItem }) {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItemLocal] = useState(selectedItemId || null);
  const setSelectedItem = (id) => { setSelectedItemLocal(id); onSelectItem?.(id); };
  const [showCreate, setShowCreate] = useState(false);
  const [createData, setCreateData] = useState({ title: '', description: '', type: 'bug', priority: 'medium' });
  const [creating, setCreating] = useState(false);

  const loadItems = useCallback(() => {
    return governance.getItems({ committee_id: committeeId, user_email: userEmail })
      .then(itemsData => setItems(extractItems(itemsData)))
      .catch(() => setItems([]));
  }, [committeeId, userEmail]);

  useEffect(() => {
    // Fetch committee and items separately so one failure doesn't block the other
    governance.getCommittee(committeeId)
      .then(d => setData(d))
      .catch(err => console.error('[Committee] Failed to load:', err.message));

    governance.getItems({ committee_id: committeeId, user_email: userEmail })
      .then(itemsData => setItems(extractItems(itemsData)))
      .catch(err => { console.error('[Committee Items] Failed to load:', err.message); setItems([]); });

    // Set loading false after both settle
    Promise.allSettled([
      governance.getCommittee(committeeId),
      governance.getItems({ committee_id: committeeId, user_email: userEmail }),
    ]).finally(() => setLoading(false));
  }, [committeeId, userEmail]);

  if (selectedItem) {
    const committeeName = data?.committee?.display_name || data?.committee?.name || 'Committee';
    return <ItemDetail itemId={selectedItem} userEmail={userEmail} committeeName={committeeName}
      onBack={() => { setSelectedItem(null); loadItems(); }} />;
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;
  if (!data?.committee) return <div className="p-8 text-center text-gray-400">Committee not found.</div>;

  const { committee, members = [], applications = [] } = data;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm mb-4">
        <button onClick={onBack} className="text-blue-600 hover:underline">Committees</button>
        <span className="text-gray-300">/</span>
        <span className="font-semibold text-gray-900">{committee.display_name || committee.name}</span>
      </nav>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{committee.display_name || committee.name}</h2>
            <p className="text-sm text-gray-500 mt-1">{committee.description}</p>
            <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
              <span>{members.length} members</span>
              <span>Quorum: {committee.quorum_size}</span>
              {applications.length > 0 && (
                <span className="text-amber-600 font-medium">{applications.length} pending application{applications.length > 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
          <button onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex-shrink-0">
            + Submit Request
          </button>
        </div>
      </div>

      {/* Submit Request form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-blue-200 p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">Submit a Request</h3>
          <div className="space-y-3">
            <input type="text" value={createData.title} onChange={e => setCreateData(d => ({ ...d, title: e.target.value }))}
              placeholder="Title (e.g. Bug: login page broken on mobile)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }} />
            <textarea value={createData.description} onChange={e => setCreateData(d => ({ ...d, description: e.target.value }))}
              placeholder="Describe the issue or request in detail..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-24 resize-none" style={{ fontSize: 16 }} />
            <div className="flex gap-2">
              <select value={createData.type} onChange={e => setCreateData(d => ({ ...d, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }}>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="security">Security</option>
                <option value="policy">Policy</option>
                <option value="legal">Legal</option>
                <option value="business">Business</option>
                <option value="other">Other</option>
              </select>
              <select value={createData.priority} onChange={e => setCreateData(d => ({ ...d, priority: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }}>
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowCreate(false); setCreateData({ title: '', description: '', type: 'bug', priority: 'medium' }); }}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              <button disabled={!createData.title.trim() || creating}
                onClick={async () => {
                  setCreating(true);
                  try {
                    await governance.createItem({ ...createData, committee_id: committeeId, user_email: userEmail, source_app: 'jubly' });
                    setShowCreate(false);
                    setCreateData({ title: '', description: '', type: 'bug', priority: 'medium' });
                    await loadItems();
                  } catch (err) {
                    // silently fail — items will refresh
                  }
                  setCreating(false);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-primary-700">
                {creating ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Members</h3>
        {members.length === 0 ? (
          <p className="text-sm text-gray-400">No members yet.</p>
        ) : (
          <div className="space-y-2">
            {members.map(m => {
              const name = m.display_name || m.user_name || m.email || m.user_email || '?';
              const email = m.email || m.user_email || '';
              return (
                <div key={m.id || m.user_id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
                      {name[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{name}</div>
                      <div className="text-xs text-gray-400">{email}</div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 capitalize">{m.role}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Items / Proposals */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          Items
          {items.length > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">{items.length}</span>}
        </h3>
        {items.length === 0 ? (
          <p className="text-sm text-gray-400">No items submitted to this committee yet. Use the button above to submit a request.</p>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const isCodeCommittee = ['product', 'engineering', 'technical'].includes(committee.name);
              const canApprove = item.status !== 'approved' && item.status !== 'rejected' && item.status !== 'executed';
              return (
              <div key={item.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 cursor-pointer flex-1" onClick={() => setSelectedItem(item.id)}>
                    <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                    {item.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {item.source_type && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                          item.source_type === 'jubly' ? 'bg-blue-100 text-blue-700' :
                          item.source_type === 'legalia' ? 'bg-amber-100 text-amber-700' :
                          item.source_type === 'pikto' ? 'bg-purple-100 text-purple-700' :
                          item.source_type === 'imagia' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{item.source_type}</span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        item.type === 'bug' ? 'bg-red-100 text-red-700' :
                        item.type === 'feature' ? 'bg-blue-100 text-blue-700' :
                        item.type === 'security' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{item.type}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}>{item.status?.replace('_', ' ')}</span>
                      {item.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[item.priority] || 'bg-gray-100 text-gray-600'}`}>{item.priority}</span>}
                      {item.submitted_by_email && <span className="text-[10px] text-gray-400">{item.submitted_by_email}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-[10px] text-gray-300">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                    {(item.vote_count > 0 || item.approve_count > 0 || item.reject_count > 0) && (
                      <div className="flex items-center gap-1.5">
                        {item.approve_count > 0 && <span className="text-[10px] text-green-600 font-medium">{item.approve_count} yes</span>}
                        {item.reject_count > 0 && <span className="text-[10px] text-red-600 font-medium">{item.reject_count} no</span>}
                        {item.vote_count > 0 && !item.approve_count && <span className="text-[10px] text-gray-400">{item.vote_count} votes</span>}
                      </div>
                    )}
                    {item.comment_count > 0 && (
                      <span className="text-[10px] text-gray-400">{item.comment_count} comments</span>
                    )}
                  </div>
                </div>
                {/* Admin quick actions */}
                {canApprove && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                    {isCodeCommittee && (
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        const btn = e.currentTarget;
                        btn.disabled = true;
                        btn.textContent = 'Processing...';
                        try {
                          await governance.adminApprove(item.id, userEmail);
                          await governance.triggerAiBuild(item);
                          await governance.setActionStatus(item.id, 'agent_executing');
                        } catch {}
                        loadItems();
                      }} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Approve & Assign to Agent
                      </button>
                    )}
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      try { await governance.adminApprove(item.id, userEmail); } catch {}
                      loadItems();
                    }} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                      Approve
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending Applications */}
      {applications.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            Pending Applications
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">{applications.length}</span>
          </h3>
          <div className="space-y-4">
            {applications.map(a => (
              <div key={a.id || a.user_id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-semibold">
                      {(a.display_name || a.email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{a.display_name || a.email?.split('@')[0]}</div>
                      <div className="text-xs text-gray-400">{a.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      await governance.approveJoin(committeeId, a.user_id, userEmail);
                      governance.getCommittee(committeeId).then(d => setData(d));
                    }}
                    className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Approve
                  </button>
                </div>
                {a.application_text && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-line">{a.application_text}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GovernanceSection({ userEmail, activeTab }) {
  const [govTab, setGovTab] = useState('inbox');
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createData, setCreateData] = useState({ title: '', description: '', committee_id: '', type: 'other', priority: 'medium' });
  const [committees, setCommittees] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (activeTab === 'governance') {
      governance.getItems({ user_email: userEmail }).then(d => setItems(extractItems(d))).catch(() => setItems([]));
      governance.getActions().then(d => setActions(Array.isArray(d?.actions) ? d.actions : [])).catch(() => setActions([]));
      governance.getCommittees(userEmail).then(d => setCommittees(Array.isArray(d?.committees) ? d.committees : [])).catch(() => setCommittees([]));
    }
  }, [activeTab, userEmail]);

  if (selectedItem) {
    return <ItemDetail itemId={selectedItem} userEmail={userEmail} onBack={() => { setSelectedItem(null); governance.getItems({ user_email: userEmail }).then(d => setItems(extractItems(d))).catch(() => setItems([])); }} />;
  }

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-4">
        {[{ key: 'inbox', label: 'Inbox' }, { key: 'actions', label: 'Actions' }].map(t => (
          <button key={t.key} onClick={() => setGovTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${govTab === t.key ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            {t.label}
            {t.key === 'inbox' && items.filter(i => i.status !== 'approved' && i.status !== 'rejected').length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-primary-600 text-white text-xs rounded-full">{items.filter(i => i.status !== 'approved' && i.status !== 'rejected').length}</span>
            )}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">
          + New Request
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">New Governance Request</h3>
          <div className="space-y-3">
            <select value={createData.committee_id} onChange={e => setCreateData(d => ({ ...d, committee_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }}>
              <option value="">Select committee...</option>
              {committees.map(c => <option key={c.id} value={c.id}>{c.display_name || c.name}</option>)}
            </select>
            <input type="text" value={createData.title} onChange={e => setCreateData(d => ({ ...d, title: e.target.value }))}
              placeholder="Request title" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }} />
            <textarea value={createData.description} onChange={e => setCreateData(d => ({ ...d, description: e.target.value }))}
              placeholder="Describe your request..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-24 resize-none" style={{ fontSize: 16 }} />
            <div className="flex gap-2">
              <select value={createData.type} onChange={e => setCreateData(d => ({ ...d, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }}>
                <option value="feature">Feature</option><option value="policy">Policy</option>
                <option value="legal">Legal</option><option value="business">Business</option><option value="other">Other</option>
              </select>
              <select value={createData.priority} onChange={e => setCreateData(d => ({ ...d, priority: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm" style={{ fontSize: 16 }}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              <button disabled={!createData.title || !createData.committee_id || creating}
                onClick={async () => {
                  setCreating(true);
                  await governance.createItem({ ...createData, user_email: userEmail });
                  setShowCreate(false); setCreateData({ title: '', description: '', committee_id: '', type: 'other', priority: 'medium' });
                  governance.getItems({ user_email: userEmail }).then(d => setItems(extractItems(d))).catch(() => {});
                  setCreating(false);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inbox */}
      {govTab === 'inbox' && (
        <div className="space-y-2">
          {items.length === 0 && <div className="p-8 text-center text-gray-400">No governance items yet.</div>}
          {items.map(item => (
            <div key={item.id} onClick={() => setSelectedItem(item.id)}
              className="p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-gray-900 truncate">{item.title}</h4>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>{item.status}</span>
                    {item.priority && <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${PRIORITY_COLORS[item.priority] || ''}`}>{item.priority}</span>}
                    <span className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {item.vote_count > 0 && <span className="text-xs text-gray-400">{item.vote_count} votes</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {govTab === 'actions' && (
        <div className="space-y-2">
          {actions.length === 0 && <div className="p-8 text-center text-gray-400">No actions to track.</div>}
          {actions.map(a => (
            <div key={a.id} onClick={() => setSelectedItem(a.id)}
              className="p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{a.title}</h4>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full mt-1 inline-block ${
                    a.action_status === 'completed' ? 'bg-green-100 text-green-700' :
                    a.action_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{a.action_status}</span>
                </div>
                {a.verdict_at && <span className="text-xs text-gray-400">{new Date(a.verdict_at).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { CommitteeList };
